import { Injectable, Optional, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { IntegerMath } from 'integer-math';
import { WorkDay } from '@core/models';
import { selectWorkDay } from '@store/selectors';
import { IIntegerPrecisionService } from '@core/interfaces/integer-precision-service.interface';
import { DecimalPrecisionCalculator, TaxUtil } from '@/core/domain';

@Injectable({
	providedIn: 'root',
})
export class IntegerPrecisionService implements IIntegerPrecisionService {
	readonly visualPrecision = 2;
	private workDaySignal: Signal<WorkDay | null | undefined> = signal(null);

	constructor(@Optional() private store?: Store) {
		if (this.store) {
			this.workDaySignal = toSignal(this.store.select(selectWorkDay), { initialValue: null });
		}
	}

	private resolvePrecision(diaLaboral?: WorkDay | number | null): number {
		return DecimalPrecisionCalculator.calculate(this.workDaySignal(), diaLaboral);
	}

	get defaultPrecision(): number {
		return this.resolvePrecision();
	}

	internalPrecision(diaLaboral?: WorkDay | number | null): number {
		return this.resolvePrecision(diaLaboral);
	}

	toInternalFromDecimalNumber(val: number, diaLaboral?: WorkDay | number | null): bigint {
		if (val === null || val === undefined) return 0n;
		const precision = this.resolvePrecision(diaLaboral);
		const math = new IntegerMath(precision);
		return math.toInteger(val.toString());
	}

	toInternalFromDecimalString(val: string, diaLaboral?: WorkDay | number | null): bigint {
		if (!val) return 0n;
		const precision = this.resolvePrecision(diaLaboral);
		const math = new IntegerMath(precision);
		return math.toInteger(val.toString());
	}

	toInternalFromScaled(
		val: bigint | string,
		sourcePrecision: number = 2,
		diaLaboral?: WorkDay | number | null
	): bigint {
		if (val === null || val === undefined || val === '') return 0n;
		const targetPrecision = this.resolvePrecision(diaLaboral);
		const bigintVal = typeof val === 'bigint' ? val : BigInt(val);
		return this.changePrecision(bigintVal, sourcePrecision, targetPrecision);
	}

	calculatePercentage(baseAmount: bigint | string, percentStr: string, diaLaboral?: WorkDay | number | null): bigint {
		const precision = this.resolvePrecision(diaLaboral);
		const math = new IntegerMath(precision);
		const base = typeof baseAmount === 'bigint' ? baseAmount : BigInt(baseAmount || 0);
		return math.percentage(base, percentStr);
	}

	toDecimalNumberFromInternal(val?: bigint | string | number | null, diaLaboral?: WorkDay | number | null): number {
		if (val === null || val === undefined) return 0;
		return Number(this.toDecimalStringFromInternal(val, diaLaboral));
	}

	toDecimalNumberFromVisual(val?: bigint | string | number | null, diaLaboral?: WorkDay | number | null): number {
		if (val === null || val === undefined) return 0;
		const precision = this.resolvePrecision(diaLaboral);
		const math = new IntegerMath(precision);
		const bigintVal = typeof val === 'bigint' ? val : BigInt(val);
		return Number(math.toDecimal(bigintVal, true));
	}

	toDecimalStringFromInternal(val?: bigint | string | number | null, diaLaboral?: WorkDay | number | null): string {
		if (val === null || val === undefined) return '0';
		const precision = this.resolvePrecision(diaLaboral);
		const bigintVal = typeof val === 'bigint' ? val : BigInt(val);
		const negative = bigintVal < 0n;
		const digits = (negative ? -bigintVal : bigintVal).toString();

		if (precision === 0) {
			return `${negative ? '-' : ''}${digits}`;
		}

		const paddedDigits = digits.padStart(precision + 1, '0');
		const integerPart = paddedDigits.slice(0, -precision);
		const fractionalPart = paddedDigits.slice(-precision);

		return `${negative ? '-' : ''}${integerPart}.${fractionalPart}`;
	}

	toDecimalStringFromVisual(val?: bigint | string | number | null, diaLaboral?: WorkDay | number | null): string {
		if (val === null || val === undefined) return '0.00';
		const precision = this.resolvePrecision(diaLaboral);
		const math = new IntegerMath(precision);
		const bigintVal = typeof val === 'bigint' ? val : BigInt(val);
		return math.toDecimal(bigintVal, true);
	}

	toScaledStringVisual(val?: bigint | string | number | null, diaLaboral?: WorkDay | number | null): string {
		if (val === null || val === undefined) return '0';
		const precision = this.resolvePrecision(diaLaboral);
		const math = new IntegerMath(precision);
		const bigintVal = typeof val === 'bigint' ? val : BigInt(val);
		return math.changePrecision(bigintVal, precision, math.visual).toString();
	}

	toScaledStringInternal(
		val?: bigint | string | number | null,
		sourcePrecision?: number,
		diaLaboral?: WorkDay | number | null
	): string {
		if (val === null || val === undefined) return '0';

		const targetInternalPrecision = this.resolvePrecision(diaLaboral);
		const fromPrecision = sourcePrecision ?? targetInternalPrecision;
		const bigintVal = typeof val === 'bigint' ? val : BigInt(val);

		return this.changePrecision(bigintVal, fromPrecision, targetInternalPrecision).toString();
	}

	toInternalFromDecimal(val: string | number | null | undefined, diaLaboral?: WorkDay | number | null): bigint {
		if (val === null || val === undefined) return 0n;
		return typeof val === 'number'
			? this.toInternalFromDecimalNumber(val, diaLaboral)
			: this.toInternalFromDecimalString(val, diaLaboral);
	}

	changePrecision(val: bigint | number | string, sourcePrecision: number, targetPrecision: number): bigint {
		const math = new IntegerMath(sourcePrecision);
		const bigintVal = typeof val === 'bigint' ? val : BigInt(val);
		return math.changePrecision(bigintVal, sourcePrecision, targetPrecision);
	}

	changePrecisionToInternal(
		val: bigint | number | string,
		sourcePrecision: number = 0,
		diaLaboral?: WorkDay | number | null
	): bigint {
		const target = this.resolvePrecision(diaLaboral);
		return this.changePrecision(val, sourcePrecision, target);
	}

	adjustTax(
		subtotal: bigint,
		percentStr: string,
		isInclusive: boolean,
		diaLaboral?: WorkDay | number | null
	): { subtotal: bigint; tax: bigint } {
		return TaxUtil.calculate(subtotal, percentStr, isInclusive, this.resolvePrecision(diaLaboral));
	}

	adjustTaxFromScaledValue(
		subtotal: bigint,
		scaledTaxRate: bigint | number | string,
		isInclusive: boolean,
		diaLaboral?: WorkDay | number | null
	): { subtotal: bigint; tax: bigint } {
    return TaxUtil.caculateFromScaledValue(subtotal, scaledTaxRate, isInclusive, this.resolvePrecision(diaLaboral));
	}

	changePrecisionsObject<T>(
		data: T,
		columns: string[],
		sourcePrecision?: number,
		targetPrecision?: number,
		diaLaboral?: WorkDay | number | null
	): T {
		if (!data) return data;
		const srcPrec = sourcePrecision ?? this.resolvePrecision(diaLaboral);
		const targetPrec = targetPrecision ?? srcPrec;
		const math = new IntegerMath(targetPrec);
		return math.toChangePrecisions(data, srcPrec, targetPrec, {
			exclude: false,
			columns,
		});
	}

	changePrecisionsObjectToInternal<T>(
		data: T,
		columns: string[],
		sourcePrecision?: number,
		diaLaboral?: WorkDay | number | null
	): T {
		const targetPrec = this.resolvePrecision(diaLaboral);
		const srcPrec = sourcePrecision ?? targetPrec;
		return this.changePrecisionsObject(data, columns, srcPrec, targetPrec, diaLaboral);
	}

	changePrecisionsObjectToVisual<T>(
		data: T,
		columns: string[],
		sourcePrecision?: number,
		diaLaboral?: WorkDay | number | null
	): T {
		const srcPrec = sourcePrecision ?? this.resolvePrecision(diaLaboral);
		const targetPrec = this.visualPrecision;
		return this.changePrecisionsObject(data, columns, srcPrec, targetPrec, diaLaboral);
	}
}
